import {
  Autocomplete,
  CircularProgress,
  styled
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { asyncAutocompleteStyles, requiredAsteriskStyles } from '../../styles/inputStyles';
import TextField from './TextField';
import { inputBaseStyles } from '../../styles/inputStyles';
import CloseIcon from './icons/CloseIcon';

// Define the option type
export type Option =  Record<string, string | number | boolean | undefined> & { value: string; label: string };

interface AsyncAutocompleteProps {
  label?: string;
  placeholder?: string;
  value: Option | null;
  onChange: (value: Option | null) => void;
  loadOptions: (searchQuery: string, otherQuery?: string) => Promise<Option[]>;
  width?: string;
  debounceMs?: number;
  otherQuery?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  className?: string;
  loadOptionsOnOpen?: boolean;
  allowCreate?: boolean;
  minCreateLength?: number;
  onCreate?: (name: string) => Promise<Option>;
  onCreateError?: (error: any) => void;
  createLabel?: (name: string) => string;
}

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  ...inputBaseStyles,
  ...requiredAsteriskStyles,
  ...asyncAutocompleteStyles,
}));

export const AsyncAutocomplete: React.FC<AsyncAutocompleteProps> = ({
  label,
  placeholder = '',
  value,
  onChange,
  loadOptions,
  width = '100%',
  debounceMs = 300,
  otherQuery = '',
  error = false,
  helperText = '',
  required = false,
  className = '',
  loadOptionsOnOpen = true,
  allowCreate = false,
  minCreateLength = 3,
  onCreate,
  onCreateError,
  createLabel = (name) => `Add "${name}"`,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<Option[]>([]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputValue);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue, debounceMs]);

  // Fetch options when search query changes
  useEffect(() => {
    let active = true;

    if (!open || (loadOptionsOnOpen && options.length > 0)) {
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const results = await loadOptions(searchQuery, otherQuery);
        if (active) {
          setOptions(results);
        }
      } catch (error) {
        console.error('Error loading options:', error);
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open, searchQuery, loadOptions, otherQuery, loadOptionsOnOpen, options.length]);

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  // Computed options with "Add ..." option if creatable
  const computedOptions = useMemo(() => {
    const typed = inputValue.trim();
    if (!allowCreate || !onCreate || loading) return options;

    if (typed.length < minCreateLength) return options;

    const typedLower = typed.toLowerCase();
    const hasExact = options.some((o) => (o.label ?? '').toString().toLowerCase() === typedLower);
    const alreadySelected = value?.label?.toString().toLowerCase() === typedLower;

    if (hasExact || alreadySelected) return options;

    const createOption: Option = {
      value: '__create__',
      label: creating ? `Adding "${typed}"...` : createLabel(typed),
      __isCreate: true,
      __createName: typed,
    };

    return [createOption, ...options];
  }, [options, allowCreate, onCreate, inputValue, loading, minCreateLength, createLabel, creating, value]);

  // Intercept selecting the create option
  const handleChange = useCallback(async (_: any, newValue: any) => {
    const opt = newValue as Option | null;

    if (opt && (opt as any).__isCreate) {
      const name = ((opt as any).__createName as string) ?? inputValue.trim();
      if (!onCreate || creating) return;

      setCreating(true);
      try {
        const created = await onCreate(name);
        setOptions((prev) => {
          const exists = prev.some((p) => p.value === created.value);
          return exists ? prev : [created, ...prev];
        });
        onChange(created);
        setInputValue('');
        setOpen(false);
      } catch (e) {
        console.error('Error creating option:', e);
        onCreateError?.(e);
      } finally {
        setCreating(false);
      }
      return;
    }

    onChange(opt);
  }, [onChange, onCreate, creating, inputValue, onCreateError]);

  return (
    <StyledAutocomplete
      open={open}
      className={className}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setLoading(false);
        setOpen(false);
      }}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={computedOptions}
      loading={loading}
      getOptionDisabled={(option) => Boolean((option as any).__isCreate) && creating}
      noOptionsText={!loadOptionsOnOpen && inputValue.length < 3 ? 'Type first 3 characters to search' : 'No options found'}
      getOptionLabel={(option) => (option as Option)?.label || ''}
      isOptionEqualToValue={(option, value) => (option as Option)?.value === (value as Option)?.value}
      popupIcon={<KeyboardArrowDownIcon sx={{ width: '16px', height: '16px' }} />}
      clearIcon={<CloseIcon sx={{ width: '16px', height: '16px' }} />}
      ListboxProps={{
        sx: {
          '& .MuiAutocomplete-option': {
            fontSize: '15px !important',
            fontWeight: 400,
            fontFamily: 'Inter',
            padding: '8px 14px',
            color: '#000000 !important'
          }
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          width={width}
          error={error}
          helperText={helperText}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {(loading || creating) ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};